'use client';

import { Copy, Checkmark, ExitPosition, Drift } from '@velocity-exchange/icons';
import { MAIN_POOL_ID, abbreviateAddress } from '@velocity-exchange/common';
import { useState } from 'react';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { AccountSwitchHotkeyExplainer } from 'src/components/Drawers/AccountsDrawer/AccountSwitchHotkeyExplainer';
import Button from 'src/components/primitives/Button/Button';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import useModal from 'src/components/Modals/hooks/useModal';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useTotalAuthorityValue from './useTotalAuthorityValue';
import { useWalletsV2 } from 'src/hooks/wallets/useWalletsV2';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import { useDisconnectWallets } from 'src/hooks/wallets/useDisconnectWallets';
import useCurrentWalletConnectionState from 'src/hooks/useCurrentWalletConnectionState';
import { copyToClipboard } from 'src/utils/browser';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { APPKIT_SOCIAL_WALLET_PROVIDER_NAME } from 'src/constants/wallets';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';
import useWalletIdentity from 'src/hooks/wallets/useWalletIdentity';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import { cj } from 'src/utils/classNames';

const AccountGeneralInformation = ({ onClose }: { onClose: () => void }) => {
	const hasAccounts = useDriftAccountStore((s) =>
		Object.values(s.accounts).some(Boolean)
	);
	const currentAccount = useCurrentUserAccount();
	const subAccounts = useLazySubAccounts();
	const mainPoolSubAccounts = subAccounts.filter(
		(account) => account.client?.getUserAccount()?.poolId === MAIN_POOL_ID
	);
	const { authorityWallet } = useWalletsV2();
	const walletConnectionState = useCurrentWalletConnectionState();
	const identity = useWalletIdentity();
	const isDevMode = useDevSwitchIsOn();

	const wallet = useDriftStore((s) => s.wallet);
	const isMagicLinkWallet = wallet.isMagicAuth;
	const isAppKitWallet = useDriftStore((s) => s.wallet.isAppKit);
	const isSocialAppKitWallet =
		isAppKitWallet &&
		wallet?.current?.adapter?.name === APPKIT_SOCIAL_WALLET_PROVIDER_NAME;
	const isExternalWallet = isMagicLinkWallet || isSocialAppKitWallet;

	const { show } = useModal();
	const handleDisconnectFromWalletsV2 = useDisconnectWallets();

	const { sessionMode } = usePrivyWalletContext();

	const [isCopied, setIsCopied] = useState(false);

	const { totalValue, delegatedTotal } = useTotalAuthorityValue();

	const handleDeposit = () => {
		show({
			type: 'deposit',
			props: {
				defaultLargestBalance: true,
			},
		});
	};

	const handleWithdraw = () => {
		show({
			type: 'withdraw',
			props: {
				defaultLargestBalance: true,
			},
		});
	};

	const handleTransfer = () => {
		show({
			type: 'transfer',
			props: {
				targetAccountKey: currentAccount?.userKey,
			},
		});
	};

	const handleDisconnect = async () => {
		onClose();
		await handleDisconnectFromWalletsV2();
		walletConnectionState.instance.update('Connecting', false);
	};

	const handleCopyAddress = () => {
		if (!isCopied) {
			copyToClipboard(authorityWallet?.publicKey?.toString() ?? '');
			setIsCopied(true);
			setTimeout(() => {
				setIsCopied(false);
			}, 1000);
		}
	};

	const absTotalValue = Math.abs(totalValue.toNum());
	const totalValueDisplay =
		absTotalValue >= 10000
			? totalValue.toMillified()
			: totalValue.toNotional().replace('$', '');

	const showPrivyNativeWalletAddress =
		isDevMode && sessionMode === 'privy-native' && authorityWallet?.publicKey;

	return (
		<>
			<BaseModal.Title
				onClose={onClose}
				endElement={
					<>
						<AccountSwitchHotkeyExplainer />
						<IconButton
							variant="tertiary"
							size="lg"
							icon={ExitPosition}
							onClick={handleDisconnect}
						/>
					</>
				}
				childrenWrapperClassName={cj(
					showPrivyNativeWalletAddress && 'flex-col items-start gap-y-xs'
				)}
			>
				<div className="typo-body-xs">{identity}</div>
				{sessionMode !== 'privy-native' && (
					<IconButton
						variant="tertiary"
						size="sm"
						icon={isCopied ? Checkmark : Copy}
						onClick={handleCopyAddress}
					/>
				)}

				{showPrivyNativeWalletAddress && (
					<div className="flex items-center gap-x-sm">
						<div className="typo-body-xs">
							{abbreviateAddress(authorityWallet.publicKey.toString())}
						</div>
						<IconButton
							variant="tertiary"
							size="sm"
							icon={isCopied ? Checkmark : Copy}
							onClick={handleCopyAddress}
						/>
					</div>
				)}
			</BaseModal.Title>
			<div className="flex flex-col justify-between gap-2xl border-b border-stroke-divider p-lg">
				<div className="flex flex-col gap-xs">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-xs text-text-default">
							<Drift className="size-5 text-text-default" />
							<Tooltip
								className="typo-body-xs"
								content={
									<div>
										This does not include values from Managed Accounts ($
										{delegatedTotal?.toMillified()}).
									</div>
								}
								disabled={!delegatedTotal}
							>
								Total Value
							</Tooltip>
						</div>
					</div>
					<div className="flex items-center gap-xs typo-heading-h4 text-text-default">
						${totalValueDisplay}
					</div>
				</div>
				<div className="flex flex-wrap gap-sm">
					<Button size="md" onClick={handleDeposit} className="min-w-25 flex-1">
						Deposit
					</Button>
					{hasAccounts && (
						<Button
							size="md"
							variant="secondary"
							onClick={handleWithdraw}
							className="min-w-25 flex-1"
						>
							Withdraw
						</Button>
					)}
					{mainPoolSubAccounts.length > 1 && (
						<Button
							size="md"
							variant="secondary"
							onClick={handleTransfer}
							className="min-w-25 flex-1"
						>
							Transfer
						</Button>
					)}
					{isExternalWallet && (
						<>
							<Button
								size="md"
								variant="secondary"
								onClick={() => show({ type: 'send_from_wallet', props: {} })}
								className="flex-1"
							>
								Send
							</Button>
							<Button
								size="md"
								variant="secondary"
								onClick={() => show({ type: 'receive_to_wallet', props: {} })}
								className="flex-1"
							>
								Receive
							</Button>
						</>
					)}
				</div>
				{isExternalWallet && (
					<div className="flex gap-sm">
						<Button
							size="md"
							variant="secondary"
							onClick={() => show({ type: 'send_from_wallet', props: {} })}
							className="w-full"
						>
							Send
						</Button>
						<Button
							size="md"
							variant="secondary"
							onClick={() => show({ type: 'receive_to_wallet', props: {} })}
							className="w-full"
						>
							Receive
						</Button>
					</div>
				)}
			</div>
		</>
	);
};

export default AccountGeneralInformation;
